import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { compose } from 'redux';

import { Success as SuccessStep } from 'components/BookingSteps/Success/Success';

import { TAppointment } from 'types/appointment';
import { TService } from 'types/service';

import { resetAppointment } from '../../actions';
import { clearServices } from '../../actions/SelectedServicesActions';
import RootReducer from '../../reducers';

type TProps = {
  appointment: TAppointment;
  clientFirstName?: string;
  clientEmail?: string;
  clientPhone?: string;
  isMultiUser: boolean;
  services: TService[];
  onClearServices: typeof clearServices;
  onResetAppointment: typeof resetAppointment;
};

const isDevelopmentRun =
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

const Success = (props: TProps) => {
  const {
    appointment,
    clientEmail,
    clientFirstName,
    clientPhone,
    isMultiUser,
    services,
    onClearServices,
    onResetAppointment,
  } = props;
  const hasMounted = useRef(!isDevelopmentRun); // Tracks if the component has been mounted fully

  const servicesNames = services
    .filter((x: { guid: string }) =>
      appointment?.service_guids?.includes(x.guid),
    )
    .map((service: { name: string }) => service.name);

  useEffect(() => {
    if (!hasMounted.current) {
      // Mark the component as mounted after the first effect
      hasMounted.current = true;
      return; // Exit early, no cleanup needed yet
    }

    onClearServices();

    // Return the cleanup function that runs only on actual unmount
    return () => {
      if (hasMounted.current) {
        onResetAppointment();
      }
    };
  }, [onClearServices, onResetAppointment]);

  return (
    <SuccessStep
      appointments={[
        {
          address: appointment?.address_information,
          appointmentGuid: appointment?.guid,
          appointmentTime: appointment?.time,
          professionalName: isMultiUser
            ? appointment?.professional?.full_name
            : undefined,
          servicesNames: servicesNames,
          status: appointment?.status,
        },
      ]}
      clientInfo={{
        firstName: clientFirstName || '',
        email: clientEmail || '',
        phone: clientPhone || '',
      }}
    />
  );
};

export default compose(
  connect(
    (state: ReturnType<typeof RootReducer>) => {
      const appointment = state.appointment as TAppointment;

      return {
        appointment: appointment,
        isMultiUser: state.publicUser?.multi_user,
        services: appointment?.professional?.services || [],
        clientFirstName: appointment?.firstName,
        clientEmail: appointment?.email,
        clientPhone: appointment?.phone,
      };
    },
    {
      onResetAppointment: resetAppointment,
      onClearServices: clearServices,
    },
  ),
)(Success);
